import { apiService } from './ApiService';
import axios, { AxiosResponse } from 'axios';
import { WebCrawlAction, IntegrationSource } from '../config/integrations'; // Assuming these types are exported from config

/** Metadata for a signed-URL file upload (shared by per-integration + admin). */
export interface SignedUploadMeta {
    name: string;
    type: string;
    columns?: Record<string, string>;
    actions?: any[];
    default_actions?: any[];
    onProgress?: (percent: number) => void;
}

export class IntegrationService {
    private static instance: IntegrationService;

    public static getInstance(): IntegrationService {
        if (!IntegrationService.instance) {
            IntegrationService.instance = new IntegrationService();
        }
        return IntegrationService.instance;
    }

    public async getSources(integrationId: string, orgId?: number): Promise<AxiosResponse<{ sources: IntegrationSource[]; isOrgScoped: boolean; isOrgAdmin: boolean }>> {
        const params = orgId !== undefined ? `?org_id=${orgId}` : '';
        return apiService.get(`/api/integrations/${integrationId}/sources${params}`);
    }

    public async createSource(integrationId: string, data: any): Promise<AxiosResponse<any>> {
        return apiService.post(`/api/integrations/${integrationId}/sources`, data);
    }

    public async updateSource(integrationId: string, sourceId: string, data: any): Promise<AxiosResponse<any>> {
        return apiService.put(`/api/integrations/${integrationId}/sources/${sourceId}`, data);
    }

    public async deleteSource(integrationId: string, sourceId: string): Promise<AxiosResponse<any>> {
        return apiService.delete(`/api/integrations/${integrationId}/sources/${sourceId}`);
    }

    public async uploadSourceFile(integrationId: string, formData: FormData): Promise<AxiosResponse<any>> {
        return apiService.post(`/api/integrations/${integrationId}/upload`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: 300000 // 5 minutes — BQ table provisioning can take time
        });
    }

    /** PUT a file straight to a GCS signed URL. Uses a bare axios instance (NOT
     *  apiService) so our Authorization header/interceptors are never sent to
     *  GCS — the signed URL carries its own auth, and extra headers break the
     *  signature. The Content-Type MUST match what was signed. */
    private async putToSignedUrl(url: string, file: File, contentType: string, onProgress?: (p: number) => void): Promise<void> {
        await axios.put(url, file, {
            headers: { 'Content-Type': contentType },
            timeout: 0, // no client timeout — large files can take a while
            transformRequest: [(d) => d], // don't let axios stringify the File
            onUploadProgress: onProgress
                ? (e) => { if (e.total) onProgress(Math.round((e.loaded / e.total) * 100)); }
                : undefined,
        });
    }

    /**
     * Large-file upload for a per-integration source: mint a signed URL, PUT the
     * file directly to GCS (bypassing Cloud Run's 32 MiB limit), then finalize
     * by handing the backend the gs:// path to ingest.
     */
    public async uploadSourceFileViaSignedUrl(integrationId: string, file: File, meta: SignedUploadMeta): Promise<AxiosResponse<any>> {
        const contentType = file.type || 'application/octet-stream';
        const urlRes = await apiService.post<{ upload_url: string; gcs_path: string }>(
            `/api/integrations/${integrationId}/upload-url`,
            { filename: file.name, content_type: contentType },
        );
        await this.putToSignedUrl(urlRes.data.upload_url, file, contentType, meta.onProgress);
        return apiService.post(`/api/integrations/${integrationId}/finalize`, {
            gcs_path: urlRes.data.gcs_path,
            name: meta.name,
            type: meta.type,
            file_name: file.name,
            file_size: file.size,
            columns: meta.columns ?? {},
            actions: meta.actions,
            default_actions: meta.default_actions,
        }, { timeout: 300000 });
    }

    /** Admin/shared counterpart of uploadSourceFileViaSignedUrl. */
    public async uploadSharedSourceFileViaSignedUrl(
        integrationId: string,
        file: File,
        meta: SignedUploadMeta & { source_type: string; shared_organization_ids?: number[] },
    ): Promise<AxiosResponse<any>> {
        const contentType = file.type || 'application/octet-stream';
        const urlRes = await apiService.post<{ upload_url: string; gcs_path: string }>(
            `/api/integrations/admin/shared/upload-url`,
            { integration_id: integrationId, filename: file.name, content_type: contentType },
        );
        await this.putToSignedUrl(urlRes.data.upload_url, file, contentType, meta.onProgress);
        return apiService.post(`/api/integrations/admin/shared/finalize`, {
            gcs_path: urlRes.data.gcs_path,
            integration_id: integrationId,
            name: meta.name,
            type: meta.type,
            source_type: meta.source_type,
            file_name: file.name,
            file_size: file.size,
            columns: meta.columns ?? {},
            actions: meta.actions,
            default_actions: meta.default_actions,
            shared_organization_ids: meta.shared_organization_ids ?? [],
        }, { timeout: 300000 });
    }

    public async testMcpConnection(config: Record<string, any>): Promise<{ success: boolean; tools?: { name: string; description: string }[]; error?: string }> {
        const { data } = await apiService.post('/api/integrations/mcp/test-connection', {
            mcpUrl: config.mcpUrl, authType: config.authType, mcpToken: config.mcpToken, customHeaders: config.customHeaders,
        }, { timeout: 40000 });
        return data;
    }

    public async testAction(integrationId: string, data: any, timeout: number = 0): Promise<AxiosResponse<any>> {
        return apiService.post(`/api/integrations/${integrationId}/test-action`, data, {
            timeout
        });
    }

    // ---------- Admin – Shared Sources ----------

    public async getSourcesForWorkflow(
        integrationId: string,
        orgId?: number,
        sharedOrgIds?: number[],
    ): Promise<{ sources: IntegrationSource[]; isSharedMode: boolean }> {
        // Shared admin mode: fetch shared sources + each selected org's own sources
        if (!orgId && sharedOrgIds !== undefined) {
            // 1. Shared (cross-org) sources
            const sharedResp = await this.getSharedSources(integrationId);
            const allShared: IntegrationSource[] = Array.isArray(sharedResp.data)
                ? sharedResp.data
                : (sharedResp.data as any).sources ?? [];
            const filteredShared = sharedOrgIds.length === 0
                ? allShared
                : allShared.filter((s: any) => {
                    const sids: number[] = s.shared_organization_ids ?? [];
                    return sids.some(id => sharedOrgIds.includes(id));
                });

            // 2. Per-org sources for each selected org
            const orgSourceArrays = await Promise.all(
                sharedOrgIds.map(async (oid) => {
                    try {
                        const r = await this.getSources(integrationId, oid);
                        const srcs: IntegrationSource[] = r.data.sources ?? [];
                        // Tag each source with a _org_id hint for display labelling
                        return srcs.map((s: any) => ({ ...s, _owner_org_id: oid }));
                    } catch {
                        return [];
                    }
                })
            );
            const orgSources = orgSourceArrays.flat();

            // Deduplicate by id (shared sources may already appear in org results)
            const seen = new Set<string | number>();
            const merged: IntegrationSource[] = [];
            for (const s of [...filteredShared, ...orgSources]) {
                if (!seen.has((s as any).id)) {
                    seen.add((s as any).id);
                    merged.push(s);
                }
            }

            return { sources: merged, isSharedMode: true };
        }
        // Org-specific mode: normal call
        const r = await this.getSources(integrationId, orgId);
        return { sources: r.data.sources ?? [], isSharedMode: false };
    }

    public async getSharedSources(integrationTypeFilter?: string): Promise<AxiosResponse<IntegrationSource[]>> {
        const params = integrationTypeFilter ? `?integration_type=${encodeURIComponent(integrationTypeFilter)}` : '';
        return apiService.get(`/api/integrations/admin/shared${params}`);
    }

    public async createSharedSource(data: {
        name: string;
        integration_type: string;
        source_type: string;
        config?: any;
        actions?: WebCrawlAction[];
        shared_organization_ids: number[];
    }): Promise<AxiosResponse<IntegrationSource>> {
        return apiService.post('/api/integrations/admin/shared', data);
    }

    public async updateSharedSource(sourceId: string, data: {
        name?: string;
        config?: any;
        actions?: WebCrawlAction[];
        shared_organization_ids?: number[];
    }): Promise<AxiosResponse<IntegrationSource>> {
        return apiService.patch(`/api/integrations/admin/shared/${sourceId}`, data);
    }

    public async deleteSharedSource(sourceId: string): Promise<AxiosResponse<any>> {
        return apiService.delete(`/api/integrations/admin/shared/${sourceId}`);
    }

    public async uploadSharedSourceFile(formData: FormData): Promise<AxiosResponse<any>> {
        return apiService.post('/api/integrations/admin/shared/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 300000,
        });
    }

    /** Replace the data file of an existing shared source (keeps the source id
     *  stable). Uploads direct-to-GCS via a signed URL, so there is no
     *  request-size limit. */
    public async replaceSharedSourceFile(
        sourceId: string,
        integrationId: string,
        file: File,
        onProgress?: (percent: number) => void,
    ): Promise<AxiosResponse<any>> {
        const contentType = file.type || 'application/octet-stream';
        const urlRes = await apiService.post<{ upload_url: string; gcs_path: string }>(
            `/api/integrations/admin/shared/upload-url`,
            { integration_id: integrationId, filename: file.name, content_type: contentType },
        );
        await this.putToSignedUrl(urlRes.data.upload_url, file, contentType, onProgress);
        return apiService.post(`/api/integrations/admin/shared/${sourceId}/finalize`, {
            gcs_path: urlRes.data.gcs_path,
            file_name: file.name,
            file_size: file.size,
        }, { timeout: 300000 });
    }
}

export const integrationService = IntegrationService.getInstance();
